import React from 'react'
import { alpha, Box, ButtonBase, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import {
  THEME_AI_STAGE_ORDER,
  ThemeAIStageState,
  ThemeAITaskStage,
} from '../../utils/themeAIStageProgress'

interface StageStepperProps {
  current: ThemeAITaskStage
  stages: Record<ThemeAITaskStage, ThemeAIStageState>
  /** One line under each stage saying what it holds, e.g. "comments.csv · 1,204 responses". */
  details: Partial<Record<ThemeAITaskStage, string>>
  onSelect: (stage: ThemeAITaskStage) => void
  /** Icons only, for the collapsed panel: the label and detail move into a tooltip. */
  compact?: boolean
}

// A step's marker: a tick once it is done (the current step included, so a
// coded task shows four ticks), else its number. The current step is filled
// or ringed in the info colour.
// Each step's accessible name: "Run step: Not started". It starts with the
// step and the word "step" so a walkthrough can click "Run step" without
// landing on "Run again", and a screen reader still hears the status.
const stepName = (label: string, summary: string) => `${label} step: ${summary}`

const StepMarker: React.FC<{ index: number; done: boolean; isCurrent: boolean }> = ({ index, done, isCurrent }) => (
  <Box
    sx={(theme) => ({
      width: 22,
      height: 22,
      mt: '1px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.72rem',
      fontWeight: 600,
      border: '1px solid',
      borderColor: done ? 'success.main' : isCurrent ? 'info.main' : 'divider',
      backgroundColor: done ? 'success.main' : isCurrent ? 'info.main' : 'transparent',
      color: done || isCurrent ? 'common.white' : 'text.secondary',
      boxShadow: done && isCurrent ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.info.main}` : 'none',
    })}
  >
    {done ? <CheckIcon sx={{ fontSize: 14 }} /> : index + 1}
  </Box>
)

// The task at a glance and the way around it: each step says whether it is
// done and what it holds, so the summary of the whole task is always on
// screen (spec #916 §1.4 — clear staged screens). It sits in the left column,
// like the Table of Contents in Tables, so the step itself gets the full
// height (Adam and Matt, 23 Sep). Collapsed, the panel keeps the markers
// alone, as a navigation rail keeps its icons (Adam, 24 Sep).
const StageStepper: React.FC<StageStepperProps> = ({ current, stages, details, onSelect, compact = false }) => (
  <Box
    component="nav"
    aria-label="Theme AI task steps"
    sx={compact
      ? { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }
      : { display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto', mx: -1 }}
  >
    {THEME_AI_STAGE_ORDER.map((stage, index) => {
      const state = stages[stage.key]
      const isCurrent = stage.key === current
      const locked = state.status === 'locked'
      const done = state.status === 'done'
      const detail = details[stage.key]
      const summary = detail || (locked ? state.lockedReason || 'Not yet' : done ? 'Done' : 'Not started')

      if (compact) {
        return (
          <Tooltip key={stage.key} title={`${stage.label} · ${summary}`} placement="right">
            <span>
              <ButtonBase
                disabled={locked}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={stepName(stage.label, summary)}
                onClick={() => onSelect(stage.key)}
                sx={(theme) => ({
                  p: 0.75,
                  borderRadius: 1,
                  opacity: locked ? 0.5 : 1,
                  backgroundColor: isCurrent ? alpha(theme.palette.info.main, 0.1) : 'transparent',
                  '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.08) },
                  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.info.main}`, outlineOffset: 1 },
                })}
              >
                <StepMarker index={index} done={done} isCurrent={isCurrent} />
              </ButtonBase>
            </span>
          </Tooltip>
        )
      }

      const button = (
        <ButtonBase
          disabled={locked}
          aria-current={isCurrent ? 'step' : undefined}
          aria-label={stepName(stage.label, summary)}
          onClick={() => onSelect(stage.key)}
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: '24px 1fr',
            columnGap: 1,
            alignItems: 'start',
            textAlign: 'left',
            width: '100%',
            px: 1,
            py: 1.25,
            borderRadius: 1,
            backgroundColor: isCurrent ? alpha(theme.palette.info.main, 0.08) : 'transparent',
            opacity: locked ? 0.5 : 1,
            transition: 'background-color 150ms ease-out',
            '&:hover': { backgroundColor: alpha(theme.palette.info.main, isCurrent ? 0.1 : 0.05) },
            '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.info.main}`, outlineOffset: 1 },
          })}
        >
          <StepMarker index={index} done={done} isCurrent={isCurrent} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: isCurrent ? 600 : 500, color: isCurrent ? 'text.primary' : 'text.secondary' }}>
              {stage.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>
              {summary}
            </Typography>
          </Box>
        </ButtonBase>
      )

      return locked && state.lockedReason ? (
        <Tooltip key={stage.key} title={state.lockedReason}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        <React.Fragment key={stage.key}>{button}</React.Fragment>
      )
    })}
  </Box>
)

export default StageStepper
